import { computed, inject, Injectable, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CenterCountDto, CenterDto, CenterService } from '@malirang/api-client';
import { BaseStore, LocalStorageService } from '@malirang/front-share';
import { toObservableSignal } from 'ngxtension/to-observable-signal';
import { lastValueFrom, map } from 'rxjs';
import { UserStore } from '../user.store';

/**
 * 로컬 스토리지에 저장되는 센터 정보의 키
 */
const CENTER_STORE_KEY = 'd3ad24ef-c4c2-4512-b26c-9514a1eb555c';

type State = {
  /**
   * 현재 접속한 센터
   */
  currentCenter: CenterDto | null;

  /**
   * 접속 가능한 모든 센터 목록
   */
  centers: CenterDto[];

  /**
   * 센터 사용자 수, 현재 구성원 수 등..
   */
  count: CenterCountDto;
};

@Injectable({ providedIn: 'root' })
export class CenterStore extends BaseStore<State> {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly userStore = inject(UserStore);
  private readonly centerService = inject(CenterService);

  private readonly user = this.userStore.user;
  private readonly localStorageKey = computed(() => {
    if (!this.user()) return '';

    return `${CENTER_STORE_KEY}-${this.user()?.id}`;
  });

  centers$ = this.state$.pipe(map((state) => state.centers));
  centers = toSignal(this.centers$);
  centerIds = computed(() => (this.centers() || []).map((c) => c.id));

  currentCenter$ = this.state$.pipe(map((state) => state.currentCenter));
  currentCenter: WritableSignal<CenterDto | null> = toObservableSignal(
    this.currentCenter$,
  );
  currentCenterId = computed(() => this.currentCenter()?.id || '');

  count$ = this.state$.pipe(map((state) => state.count));
  count = toSignal(this.count$);

  /**
   * @description 이용중인 사용자 수
   */
  acceptedUsersCount = computed(() => this.count()?.acceptedUsersCount || 0);

  constructor() {
    super({
      currentCenter: null,
      centers: [],
      count: {
        acceptedUsersCount: 0,
        allUsersCount: 0,
        pendingInviteLogsCount: 0,
        pendingJoinLogsCount: 0,
      },
    });
  }

  /**
   * @name fetchCenters
   * @description 센터 목록을 가져옵니다.
   * @returns {Promise<void>}
   */
  async fetchCenters(): Promise<void> {
    const centers: CenterDto[] = await lastValueFrom(
      this.centerService.userCenterControllerGetMyCenterList(),
    );

    this.setCenters(centers);
  }

  /**
   * @name fetchCount
   * @description (센터 사용자 수, 현재 구성원 수 등..)을 가져옵니다.
   * @returns {Promise<void>}
   */
  async fetchCount(): Promise<void> {
    const centerCount: CenterCountDto = await lastValueFrom(
      this.centerService.userCenterControllerGetCount({
        centerId: this.currentCenterId(),
      }),
    );

    this.setCount(centerCount);
  }

  setCount(count: CenterCountDto) {
    this.updateState({ count });
  }

  /**
   * @name initCurrentCenter
   * @description 센터 초기화
   * @returns {void}
   */
  private initCurrentCenter(): void {
    const centers = this.centers() || [];
    if (!centers || centers.length === 0) return;

    const centerId = this.localStorageService.get(this.localStorageKey());
    if (centerId) {
      const center = centers.find((c) => c.id === centerId);
      if (center) {
        this.setCurrentCenter(center);
      } else {
        this.setCurrentCenter(centers[0]);
      }
    } else {
      this.setCurrentCenter(centers[0]);
    }
  }

  setCurrentCenter(center: CenterDto) {
    if (center) {
      this.updateState({ currentCenter: center });

      if (this.localStorageKey()) {
        this.localStorageService.set(this.localStorageKey(), center.id);
      }
    }
  }

  setCenters(centers: CenterDto[]) {
    if (!this.user()) {
      return;
    }

    this.updateState({ centers });

    this.initCurrentCenter();
  }
}
